// src/pages/TestPage.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Container, useTheme, Grid
} from '@mui/material';
import { toast } from 'react-toastify';
import debounce from 'lodash.debounce';

// API Services
import { widgetTemplateService } from '../api/widgetTemplateService';
import { widgetVersionService } from '../api/widgetVersionService';

// Hooks
import { useModels } from '../hooks/useModels';

// Page-specific Components
import TestPageHeader from '../components/testpage/TestPageHeader';
import WidgetLoaderControls from '../components/testpage/WidgetLoaderControls';
import TestPageInstructions from '../components/testpage/TestPageInstructions';
import LiveConfigPanel from '../components/testpage/LiveConfigPanel';

// Constants
const WIDGET_GLOBAL_OBJECT = 'banty';
const LIVE_CONFIG_STORAGE_KEY = 'bantyWidgetLiveConfig_v4_full_testpage';
const TESTPAGE_LAST_MODE_KEY = 'testPage_lastSelectedMode_v1';
const TESTPAGE_LAST_TEMPLATE_ID_KEY = 'testPage_lastSelectedTemplateId_v1';
const TESTPAGE_LAST_VERSION_ID_KEY = 'testPage_lastSelectedVersionId_v2';
const WIDGET_LOADER_SCRIPT_ID = 'banty-loader-v2-script';
const WIDGET_MOUNT_DIV_ID = 'banty-chat-widget-mount-point';

// Initial config state for the LiveConfigPanel (same as before)
const initialLiveConfig = {
  // --- Bot & General Info ---
  widgetTitle: "Awesome AI Assistant",
  botAvatarImage: "https://images.unsplash.com/photo-1601839900160-81640747440c?w=150&h=150&fit=crop&crop=faces",
  websiteName: "My Awesome Site",
  collapsedMessage: "Need Help? Ask {botName}!",
  inputPlaceholder: "Type your message here...",
  placeholderWelcome: "Initializing chat...",
  welcomeStrategy: 'default',
  defaultWelcome: "Hello there! I'm {botName}. How can I assist you today on {websiteName}?",
  ruleBasedWelcome: [
    { if_url_contains: '/pricing', if_hostname_is: '', message: "Looking at our pricing? I can help explain our plans!" },
    { if_url_contains: '/support', if_hostname_is: '', message: "Welcome to support! What can I help you find?" },
  ],
  llmWelcomePrompt: 'You are {botName}, a friendly AI assistant for {websiteName}. A user has just landed on the page: {page_url}. Craft a short, engaging, and contextually relevant welcome message offering assistance.',
  llmWelcomeModel: '',
  appearance: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    fontSizeBase: "15px",
    widgetShadow: "0 8px 24px rgba(0, 0, 0, 0.12), 0 3px 6px rgba(0, 0, 0, 0.08)",
    bubbleShadow: "0 1px 3px rgba(0,0,0,0.06)",
    widgetMaxWidth: "380px",
    widgetMaxHeight: "640px",
    defaultMinimized: false,
    autoOpenDelay: 700,
    showCollapsedThinking: true,
    maxMessageLength: 2500,
  },
  borderRadius: { small: "6px", medium: "12px", large: "18px", full: "9999px" },
  colors: {
    primary: "#007AFF", primaryGradient: "linear-gradient(145deg, #0A84FF, #007AFF)", focusRingColor: "rgba(0, 122, 255, 0.3)",
    widgetBackground: "#FFFFFF", messagesBackground: "#F9F9FB",
    textColorPrimary: "#1A1A1A", textColorSecondary: "#555555", textColorOnPrimary: "#FFFFFF",
    userBubbleBackground: "#007AFF", textColorUserBubble: "#FFFFFF",
    botBubbleBackground: "#ECECEC", textColorBotBubble: "#1A1A1A", botBubbleBorder: "transparent",
    systemBubbleBackground: "#F0F2F5", systemBubbleColor: "#3C3C3F", systemBubbleBorder: "transparent",
    borderColor: "#DCDCDC",
  },
  showThinkingProcess: true, 
  streamingEnabled: true,
  markdownSupport: { enableUserMarkdown: false, enableAssistantMarkdown: true },
  errorMessages: { default: "I seem to be having a little trouble. Please try again.", unauthorized: "Your session has timed out. Please refresh." },
  themeColor: "#007AFF", 
  position: 'bottom-right', 
  contextPrepending: { url: true, hostname: false },
};

const TestPage = () => {
  const theme = useTheme();

  // Widget lifecycle state
  const [widgetDetected, setWidgetDetected] = useState(false);
  const [checkingWidget, setCheckingWidget] = useState(false); 
  const [widgetLoadError, setWidgetLoadError] = useState(null);
  const [resolvedVersionId, setResolvedVersionId] = useState(null); 
  const [resolvedAgentId, setResolvedAgentId] = useState(null);

  // DB interaction state
  const [savingToDB, setSavingToDB] = useState(false);

  // Data for selectors
  const [allWidgetTemplates, setAllWidgetTemplates] = useState([]);
  const [allWidgetVersions, setAllWidgetVersions] = useState([]); 
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Selection state for WidgetLoaderControls
  const [loadMode, setLoadMode] = useState(() => localStorage.getItem(TESTPAGE_LAST_MODE_KEY) || 'version'); 
  const [selectedTemplateId, setSelectedTemplateId] = useState(() => localStorage.getItem(TESTPAGE_LAST_TEMPLATE_ID_KEY) || '');
  const [selectedVersionId, setSelectedVersionId] = useState(() => localStorage.getItem(TESTPAGE_LAST_VERSION_ID_KEY) || ''); 

  // LLM Models for LiveConfigPanel
  const [allModels, loadingModels, modelError] = useModels();

  // Live config state for LiveConfigPanel
  const [liveWidgetConfig, setLiveWidgetConfig] = useState(() => {
    const savedConfig = localStorage.getItem(LIVE_CONFIG_STORAGE_KEY);
    if (savedConfig) {
      try {
        const parsedSaved = JSON.parse(savedConfig);
        const mergedConfig = {
          ...initialLiveConfig, ...parsedSaved,
          appearance: { ...initialLiveConfig.appearance, ...(parsedSaved.appearance || {}) },
          borderRadius: { ...initialLiveConfig.borderRadius, ...(parsedSaved.borderRadius || {}) },
          colors: { ...initialLiveConfig.colors, ...(parsedSaved.colors || {}) },
          markdownSupport: { ...initialLiveConfig.markdownSupport, ...(parsedSaved.markdownSupport || {}) },
          errorMessages: { ...initialLiveConfig.errorMessages, ...(parsedSaved.errorMessages || {}) },
          contextPrepending: { ...initialLiveConfig.contextPrepending, ...(parsedSaved.contextPrepending || {}) },
          ruleBasedWelcome: Array.isArray(parsedSaved.ruleBasedWelcome) ? parsedSaved.ruleBasedWelcome : initialLiveConfig.ruleBasedWelcome,
          llmWelcomePrompt: parsedSaved.llmWelcomePrompt !== undefined ? parsedSaved.llmWelcomePrompt : initialLiveConfig.llmWelcomePrompt,
          llmWelcomeModel: parsedSaved.llmWelcomeModel !== undefined ? parsedSaved.llmWelcomeModel : initialLiveConfig.llmWelcomeModel,
        };
        if (mergedConfig.colors?.primary) mergedConfig.themeColor = mergedConfig.colors.primary;
        return mergedConfig;
      } catch (e) { 
        console.error("TestPage: Failed to parse saved live config:", e); 
        localStorage.removeItem(LIVE_CONFIG_STORAGE_KEY); 
      }
    }
    return JSON.parse(JSON.stringify(initialLiveConfig));
  });

  // --- CALLBACKS for WidgetLoaderControls ---
  const handleLoadModeChange = (newMode) => {
    setLoadMode(newMode);
    setSelectedVersionId(''); // Reset selections when mode changes
    setSelectedTemplateId('');
    setAllWidgetVersions([]); // Clear versions list as it might need refetching or filtering
  };
  const handleSelectedVersionChange = (versionId) => setSelectedVersionId(versionId);
  const handleSelectedTemplateChange = (templateId) => setSelectedTemplateId(templateId);


  // --- DATA FETCHING LOGIC ---
  useEffect(() => { 
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      const res = await widgetTemplateService.listTemplates();
      if (!res.isError && Array.isArray(res)) {
        setAllWidgetTemplates(res.sort((a,b) => (a.name || '').localeCompare(b.name || '')));
      } else {
        toast.error(`Could not load widget templates: ${res.message || 'Unknown error'}`);
        setAllWidgetTemplates([]);
      }
      setLoadingTemplates(false);
    };
    fetchTemplates();
  }, []);

  useEffect(() => { 
    const fetchVersions = async () => {
      setLoadingVersions(true);
      setAllWidgetVersions([]); 
      if (loadMode === 'version') { 
        let versionsList = [];
        if (allWidgetTemplates.length > 0) {
            for (const template of allWidgetTemplates) {
                const versionsResult = await widgetVersionService.listVersionsByTemplate(template.id);
                if (!versionsResult.isError && Array.isArray(versionsResult)) {
                  versionsResult.forEach(v => {
                      const displayName = v.version_name || v.name || "Unnamed Version";
                      // Assuming template object might not be fully populated in v from listVersionsByTemplate
                      // So, find the template from allWidgetTemplates to get its name
                      const parentTemplate = allWidgetTemplates.find(t => t.id === v.template_id);
                      const templateNameForDisplay = parentTemplate?.name || v.template?.name || 'Template N/A';
                      versionsList.push({ ...v, displayName: `${templateNameForDisplay} - ${displayName}` });
                  });
                } else if (versionsResult.isError) {
                    console.warn(`Failed to load versions for template ${template.id}: ${versionsResult.message}`);
                }
            }
            versionsList.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
        }
        setAllWidgetVersions(versionsList);

      } else if (loadMode === 'template' && selectedTemplateId) { 
        const res = await widgetVersionService.listVersionsByTemplate(selectedTemplateId);
        if (!res.isError && Array.isArray(res)) {
          setAllWidgetVersions(res.sort((a,b) => (a.version_name || a.name || '').localeCompare(b.version_name || b.name || '')));
        } else {
          toast.error(`Could not load versions for template ${selectedTemplateId.slice(-6)}: ${res.message || ''}`);
        }
      }
      setLoadingVersions(false);
    };

    if (loadMode === 'version' && allWidgetTemplates.length > 0 && !loadingTemplates) fetchVersions();
    if (loadMode === 'template' && selectedTemplateId) fetchVersions();
    if (loadMode === 'template' && !selectedTemplateId) {
        setAllWidgetVersions([]); 
        setLoadingVersions(false);
    }
  }, [loadMode, selectedTemplateId, allWidgetTemplates, loadingTemplates]); 

  // --- PERSIST SELECTIONS TO LOCALSTORAGE ---
  useEffect(() => { localStorage.setItem(TESTPAGE_LAST_MODE_KEY, loadMode); }, [loadMode]);
  useEffect(() => { if (selectedTemplateId) localStorage.setItem(TESTPAGE_LAST_TEMPLATE_ID_KEY, selectedTemplateId); else localStorage.removeItem(TESTPAGE_LAST_TEMPLATE_ID_KEY) }, [selectedTemplateId]);
  useEffect(() => { if (selectedVersionId) localStorage.setItem(TESTPAGE_LAST_VERSION_ID_KEY, selectedVersionId); else localStorage.removeItem(TESTPAGE_LAST_VERSION_ID_KEY) }, [selectedVersionId]);

  // --- WIDGET LOADING & CLEANUP LOGIC ---
  const getScriptDataAttributes = () => {
    const platformApiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const attributes = {
      'data-mount-id': WIDGET_MOUNT_DIV_ID,
      'data-platform-api-base': platformApiBase,
    };
    if (loadMode === 'version' && selectedVersionId) {
      attributes['data-widget-id'] = selectedVersionId;
    } else if (loadMode === 'template' && selectedTemplateId) {
      attributes['data-template-id'] = selectedTemplateId;
    }
    return attributes;
  };

  useEffect(() => {
    const cleanupWidgetInstance = () => {
      setWidgetDetected(false); setCheckingWidget(false); setWidgetLoadError(null);
      setResolvedVersionId(null); setResolvedAgentId(null);
      const scriptTag = document.getElementById(WIDGET_LOADER_SCRIPT_ID);
      if (scriptTag) scriptTag.remove();
      const mountDiv = document.getElementById(WIDGET_MOUNT_DIV_ID);
      if (mountDiv) { while (mountDiv.firstChild) mountDiv.removeChild(mountDiv.firstChild); }
      if (window.AGENT_PLATFORM_LOADER_V2_RUNNING) delete window.AGENT_PLATFORM_LOADER_V2_RUNNING;
      if (window.AGENT_PLATFORM_WIDGET_CONFIG) delete window.AGENT_PLATFORM_WIDGET_CONFIG;
      if (window[WIDGET_GLOBAL_OBJECT]) delete window[WIDGET_GLOBAL_OBJECT];
    };

    const loadWidgetInstance = () => {
      const attributes = getScriptDataAttributes();
      if (!attributes['data-widget-id'] && !attributes['data-template-id']) {
        if (widgetDetected || checkingWidget) toast.info("Select a version or template to load the widget.");
        cleanupWidgetInstance(); return;
      }
      console.log(`TestPage: Attempting to load widget with attributes:`, attributes);
      setCheckingWidget(true); setWidgetLoadError(null); setResolvedVersionId(null); setResolvedAgentId(null);

      const script = document.createElement('script');
      script.id = WIDGET_LOADER_SCRIPT_ID;
      script.src = `${attributes['data-platform-api-base']}/static/loader-v2.js`;
      Object.entries(attributes).forEach(([key, value]) => script.setAttribute(key, value));
      script.async = true; script.defer = true;
      script.onload = () => console.log("TestPage: Banty loader-v2.js script tag executed 'onload'.");
      script.onerror = (err) => {
        const errorMsg = "Widget script failed to load. Check console and network tab (CORS, 404).";
        console.error("TestPage: Failed to load Banty loader-v2.js script.", err);
        toast.error(errorMsg); setWidgetLoadError(errorMsg); setCheckingWidget(false); cleanupWidgetInstance();
      };
      document.head.appendChild(script);
    };

    cleanupWidgetInstance();
    if ((loadMode === 'version' && selectedVersionId) || (loadMode === 'template' && selectedTemplateId)) {
      loadWidgetInstance();
    }
    return cleanupWidgetInstance;
  }, [loadMode, selectedVersionId, selectedTemplateId]); 

  // --- WIDGET DETECTION POLLING & CONFIG EXTRACTION ---
  useEffect(() => {
    if (!checkingWidget) {
      if (!((loadMode === 'version' && selectedVersionId) || (loadMode === 'template' && selectedTemplateId)) && widgetDetected) setWidgetDetected(false);
      return;
    }
    let detectionIntervalId = null; let detectionTimeoutId = null;
    const checkWidgetPresenceAndConfig = () => {
      if (window[WIDGET_GLOBAL_OBJECT] && typeof window[WIDGET_GLOBAL_OBJECT].updateConfig === 'function') {
        if (!widgetDetected) { setWidgetDetected(true); toast.success("Widget detected and ready!"); }
        setCheckingWidget(false);
        if (window.AGENT_PLATFORM_WIDGET_CONFIG) {
          const cfg = window.AGENT_PLATFORM_WIDGET_CONFIG;
          console.log("TestPage: Widget config from window:", cfg);
          if (cfg.versionId) setResolvedVersionId(cfg.versionId);
          if (cfg.agentId) setResolvedAgentId(cfg.agentId);
          if (cfg.versionId && ( (loadMode === 'template' && selectedTemplateId) || (loadMode === 'version' && selectedVersionId === cfg.versionId) ) ) {
            const widgetInitialConfig = cfg.config;
            if (widgetInitialConfig) {
              const mergedConfig = {
                  ...initialLiveConfig, ...widgetInitialConfig,
                  appearance: { ...initialLiveConfig.appearance, ...(widgetInitialConfig.appearance || {}) },
                  borderRadius: { ...initialLiveConfig.borderRadius, ...(widgetInitialConfig.borderRadius || {}) },
                  colors: { ...initialLiveConfig.colors, ...(widgetInitialConfig.colors || {}) },
                  markdownSupport: { ...initialLiveConfig.markdownSupport, ...(widgetInitialConfig.markdownSupport || {}) },
                  errorMessages: { ...initialLiveConfig.errorMessages, ...(widgetInitialConfig.errorMessages || {}) },
                  contextPrepending: { ...initialLiveConfig.contextPrepending, ...(widgetInitialConfig.contextPrepending || {}) },
                  ruleBasedWelcome: Array.isArray(widgetInitialConfig.ruleBasedWelcome) ? widgetInitialConfig.ruleBasedWelcome : initialLiveConfig.ruleBasedWelcome,
                  llmWelcomePrompt: widgetInitialConfig.llmWelcomePrompt !== undefined ? widgetInitialConfig.llmWelcomePrompt : initialLiveConfig.llmWelcomePrompt,
                  llmWelcomeModel: widgetInitialConfig.llmWelcomeModel !== undefined ? widgetInitialConfig.llmWelcomeModel : initialLiveConfig.llmWelcomeModel,
              };
              if(mergedConfig.colors?.primary) mergedConfig.themeColor = mergedConfig.colors.primary; 
              setLiveWidgetConfig(mergedConfig);
              toast.info("Live Config Panel synced with loaded widget's initial settings.");
            }
          }
        } else { console.warn("TestPage: Widget detected, but window.AGENT_PLATFORM_WIDGET_CONFIG not available."); }
        clearInterval(detectionIntervalId); clearTimeout(detectionTimeoutId);
      }
    };
    checkWidgetPresenceAndConfig();
    if (!widgetDetected) {
      detectionIntervalId = setInterval(checkWidgetPresenceAndConfig, 1200);
      detectionTimeoutId = setTimeout(() => {
        clearInterval(detectionIntervalId); if (!widgetDetected) checkWidgetPresenceAndConfig();
        if (!widgetDetected) {
          setCheckingWidget(false);
          const errorMsg = `Widget (${WIDGET_GLOBAL_OBJECT}) not detected after timeout. Issues: ID, domain, script error, network.`;
          console.warn("TestPage: " + errorMsg); setWidgetLoadError(errorMsg); toast.warn(`Widget not detected. Check console.`);
        }
      }, 8000);
    }
    return () => { clearInterval(detectionIntervalId); clearTimeout(detectionTimeoutId); };
  }, [checkingWidget, widgetDetected, loadMode, selectedTemplateId, selectedVersionId]); 

  // --- LiveConfigPanel Handlers ---
  const debouncedUpdateWidget = useRef(
    debounce((configToApply) => {
      if (window[WIDGET_GLOBAL_OBJECT] && typeof window[WIDGET_GLOBAL_OBJECT].updateConfig === 'function') {
        const liveUpdatePayload = {
          widgetTitle: configToApply.widgetTitle, botAvatarImage: configToApply.botAvatarImage,
          collapsedMessage: configToApply.collapsedMessage,
          websiteName: configToApply.websiteName, showThinkingProcess: configToApply.showThinkingProcess,
          streamingEnabled: configToApply.streamingEnabled, inputPlaceholder: configToApply.inputPlaceholder,
          fontFamily: configToApply.appearance?.fontFamily, fontSizeBase: configToApply.appearance?.fontSizeBase,
          widgetShadow: configToApply.appearance?.widgetShadow, bubbleShadow: configToApply.appearance?.bubbleShadow,
          widgetMaxWidth: configToApply.appearance?.widgetMaxWidth, widgetMaxHeight: configToApply.appearance?.widgetMaxHeight,
          defaultMinimized: configToApply.appearance?.defaultMinimized, autoOpenDelay: configToApply.appearance?.autoOpenDelay,
          showCollapsedThinking: configToApply.appearance?.showCollapsedThinking, maxMessageLength: configToApply.appearance?.maxMessageLength,
          borderRadiusSmall: configToApply.borderRadius?.small, borderRadiusMedium: configToApply.borderRadius?.medium,
          borderRadiusLarge: configToApply.borderRadius?.large, borderRadiusFull: configToApply.borderRadius?.full,
          colors: configToApply.colors ? { ...configToApply.colors } : undefined,
          enableUserMarkdown: configToApply.markdownSupport?.enableUserMarkdown, enableAssistantMarkdown: configToApply.markdownSupport?.enableAssistantMarkdown,
          errorMessageDefault: configToApply.errorMessages?.default, errorMessageUnauthorized: configToApply.errorMessages?.unauthorized,
        };
        Object.keys(liveUpdatePayload).forEach(key => liveUpdatePayload[key] === undefined && delete liveUpdatePayload[key]);
        if (liveUpdatePayload.colors && Object.keys(liveUpdatePayload.colors).length === 0) delete liveUpdatePayload.colors;
        try {
          window[WIDGET_GLOBAL_OBJECT].updateConfig(liveUpdatePayload);
          toast.info("Live widget updated (some settings need reload).");
        } catch (e) { console.error(`TestPage: Error calling updateConfig:`, e); toast.error(`Update failed: ${e.message}`); }
      } else if (widgetDetected) { console.warn(`TestPage: window.${WIDGET_GLOBAL_OBJECT}.updateConfig not found.`); }
    }, 750)
  ).current;
  
  useEffect(() => { if (widgetDetected) debouncedUpdateWidget(liveWidgetConfig); }, [liveWidgetConfig, widgetDetected, debouncedUpdateWidget]);

  const handleLiveConfigChange = useCallback((fieldOrPath, value, ruleIndex, ruleField) => {
    setLiveWidgetConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      if (fieldOrPath === 'ruleBasedWelcome') {
        const newRules = [...(newConfig.ruleBasedWelcome || [])];
        if (ruleIndex !== undefined && ruleField !== undefined) { if (newRules[ruleIndex]) newRules[ruleIndex][ruleField] = value; }
        else if (ruleIndex !== undefined && value === null) { newRules.splice(ruleIndex, 1); }
        else if (value && typeof value === 'object' && ruleIndex === undefined) { newRules.push(value); }
        newConfig.ruleBasedWelcome = newRules;
      } else if (fieldOrPath.includes('.')) {
        const keys = fieldOrPath.split('.'); let currentLevel = newConfig;
        keys.forEach((key, index) => {
          if (index === keys.length - 1) currentLevel[key] = value;
          else { if (!currentLevel[key] || typeof currentLevel[key] !== 'object') currentLevel[key] = {}; currentLevel = currentLevel[key]; }
        });
      } else newConfig[fieldOrPath] = value;
      if (fieldOrPath === 'colors.primary') newConfig.themeColor = value;
      else if (fieldOrPath === 'themeColor' && newConfig.colors) newConfig.colors.primary = value;
      return newConfig;
    });
  }, []);

  const handleLoadPreset = (presetId) => {
    let newConfig = JSON.parse(JSON.stringify(initialLiveConfig)); let presetName = "Unknown";
    switch (presetId) {
      case 'default_minimal': presetName = "Default Minimal"; newConfig.widgetTitle = "AI Assistant"; newConfig.defaultWelcome = "Hi! How can I help?"; newConfig.appearance.autoOpenDelay = 1500; break;
      case 'bright_friendly': presetName = "Bright & Friendly"; newConfig.widgetTitle = "Friendly Helper"; newConfig.appearance.autoOpenDelay=500; newConfig.colors.primary = "#FF6F00"; newConfig.colors.userBubbleBackground = "#FF6F00"; newConfig.colors.primaryGradient = "linear-gradient(135deg, #FFA726, #FF6F00)"; newConfig.botAvatarImage = "https://source.unsplash.com/150x150/?smiley,robot"; newConfig.defaultWelcome = "Hey there! I'm your friendly helper. What's on your mind?"; break;
      case 'dark_basic': presetName = "Dark Mode (Basic)"; newConfig.widgetTitle = "Night Assistant"; newConfig.colors.primary = "#4D82E9"; newConfig.colors.widgetBackground = "#23272A"; newConfig.colors.messagesBackground = "#2C2F33"; newConfig.colors.textColorPrimary = "#FFFFFF"; newConfig.colors.textColorSecondary = "#B9BBBE"; newConfig.colors.textColorOnPrimary = "#FFFFFF"; newConfig.colors.userBubbleBackground = "#4D82E9"; newConfig.colors.textColorUserBubble = "#FFFFFF"; newConfig.colors.botBubbleBackground = "#3A3E42"; newConfig.colors.textColorBotBubble = "#E0E0E0"; newConfig.colors.borderColor = "#42464A"; newConfig.defaultWelcome = "Good evening! How can I assist you in the dark?"; break;
      default: toast.warn(`Unknown preset: ${presetId}`); return;
    }
    if(newConfig.colors?.primary) newConfig.themeColor = newConfig.colors.primary;
    setLiveWidgetConfig(newConfig); toast.success(`Preset "${presetName}" loaded into panel.`);
  };

  const handleSaveLiveConfigToLocalStorage = () => { 
      localStorage.setItem(LIVE_CONFIG_STORAGE_KEY, JSON.stringify(liveWidgetConfig)); 
      toast.success('Live config saved to browser local storage!'); 
  };
  const handleResetLiveConfigToDefaults = () => { 
      localStorage.removeItem(LIVE_CONFIG_STORAGE_KEY); 
      setLiveWidgetConfig(JSON.parse(JSON.stringify(initialLiveConfig))); 
      toast.info('Live config panel reset to defaults.'); 
  };

  const handleSaveConfigToDB = async () => {
    const targetVersionId = resolvedVersionId;
    if (!targetVersionId) {
      toast.warn("Cannot save to DB: No widget version resolved. Load a widget first."); return;
    }
    setSavingToDB(true); toast.info(`Saving panel config to DB for version: ...${targetVersionId.slice(-6)}`);
    const payloadToSave = JSON.parse(JSON.stringify(liveWidgetConfig));
    if (payloadToSave.colors?.primary) payloadToSave.themeColor = payloadToSave.colors.primary;
    else if (payloadToSave.themeColor) {
        if(!payloadToSave.colors) payloadToSave.colors = {};
        payloadToSave.colors.primary = payloadToSave.themeColor;
        if (!payloadToSave.colors.userBubbleBackground) payloadToSave.colors.userBubbleBackground = payloadToSave.themeColor;
    }
    // Flatten borderRadius for DB if backend expects flat structure for these
    // Example: payloadToSave.borderRadiusSmall = payloadToSave.borderRadius.small; delete payloadToSave.borderRadius;
    // For now, assuming backend handles the 'borderRadius' object within 'config'.
    // If backend expects flat keys like 'borderRadiusSmall', this transformation is needed here.
    // The current widgetVersionService.updateVersion handles a 'config' object, so direct pass is likely fine.

    const result = await widgetVersionService.updateVersion(targetVersionId, payloadToSave); 
    setSavingToDB(false);
    if (result.isError) { toast.error(`DB Save Failed: ${result.message || 'Error'}`); console.error("DB Save Error:", result); }
    else { toast.success(`Config saved to DB for version: ${result.version_name || targetVersionId.slice(-6)}!`); console.log("DB Save Success:", result); }
  };
  
  const fetchAndSetLiveConfigForVersion = async (versionIdToLoad) => {
      if (!versionIdToLoad) return;
      toast.info(`Fetching config from DB for version ...${versionIdToLoad.slice(-6)}...`);
      setSavingToDB(true); // Indicate loading activity
      const versionData = await widgetVersionService.getVersion(versionIdToLoad);
      setSavingToDB(false);
      if (versionData.isError) { toast.error(`Failed to load config from DB: ${versionData.message}`); return; }
      if (versionData.config) {
          const dbConfig = versionData.config;
          const mergedConfig = {
            ...initialLiveConfig, ...dbConfig, 
            appearance: { ...initialLiveConfig.appearance, ...(dbConfig.appearance || {}) },
            borderRadius: { ...initialLiveConfig.borderRadius, ...(dbConfig.borderRadius || {}) },
            colors: { ...initialLiveConfig.colors, ...(dbConfig.colors || {}) },
            markdownSupport: { ...initialLiveConfig.markdownSupport, ...(dbConfig.markdownSupport || {}) },
            errorMessages: { ...initialLiveConfig.errorMessages, ...(dbConfig.errorMessages || {}) },
            contextPrepending: { ...initialLiveConfig.contextPrepending, ...(dbConfig.contextPrepending || {}) },
            ruleBasedWelcome: Array.isArray(dbConfig.ruleBasedWelcome) ? dbConfig.ruleBasedWelcome : initialLiveConfig.ruleBasedWelcome,
            llmWelcomePrompt: dbConfig.llmWelcomePrompt !== undefined ? dbConfig.llmWelcomePrompt : initialLiveConfig.llmWelcomePrompt,
            llmWelcomeModel: dbConfig.llmWelcomeModel !== undefined ? dbConfig.llmWelcomeModel : initialLiveConfig.llmWelcomeModel,
          };
          if(versionData.version_name) mergedConfig.widgetTitle = versionData.version_name; 
          if(mergedConfig.colors?.primary) mergedConfig.themeColor = mergedConfig.colors.primary; 
          setLiveWidgetConfig(mergedConfig);
          toast.success(`Config for version ...${versionIdToLoad.slice(-6)} loaded into panel from DB.`);
      } else {
          toast.warn(`Version ...${versionIdToLoad.slice(-6)} has no 'config' object in DB. Panel reset.`);
          setLiveWidgetConfig(JSON.parse(JSON.stringify(initialLiveConfig)));
      }
  };

  // Props for WidgetLoaderControls
  const widgetLoaderControlsProps = {
    loadMode, selectedVersionId, selectedTemplateId, allWidgetTemplates, allWidgetVersions,
    loadingTemplates, loadingVersions, checkingWidget, widgetDetected, widgetLoadError,
    resolvedVersionId, resolvedAgentId, savingToDB, theme,
    onLoadModeChange: handleLoadModeChange,
    onSelectedVersionChange: handleSelectedVersionChange,
    onSelectedTemplateChange: handleSelectedTemplateChange,
    onFetchAndSetLiveConfigForVersion: fetchAndSetLiveConfigForVersion,
    widgetGlobalObjectName: WIDGET_GLOBAL_OBJECT,
  };

  // Props for LiveConfigPanel
  const liveConfigPanelProps = {
    config: liveWidgetConfig,
    onConfigChange: handleLiveConfigChange,
    widgetLoaded: widgetDetected,
    onLoadPreset: handleLoadPreset,
    onSaveConfig: handleSaveLiveConfigToLocalStorage,
    onResetConfig: handleResetLiveConfigToDefaults,
    onSaveConfigToDB: handleSaveConfigToDB,
    isSavingToDB: savingToDB,
    allModels: allModels || [],
    disableSaveToDB: !resolvedVersionId || savingToDB,
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, bgcolor: theme.palette.background.default, minHeight: 'calc(100vh - 64px)' }}>
      <Container maxWidth="xl">
        <div id={WIDGET_MOUNT_DIV_ID} /> {/* Widget mounts here */}
        
        <TestPageHeader modelError={modelError} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}> {/* Left Column */}
            <WidgetLoaderControls {...widgetLoaderControlsProps} />
            <TestPageInstructions theme={theme} />
          </Grid>

          <Grid item xs={12} md={8}> {/* Right Column */}
            <LiveConfigPanel {...liveConfigPanelProps} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TestPage;